import { ElementsConsumer, useStripe } from "@stripe/react-stripe-js";
import { Form, Formik } from "formik";
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { connect } from "react-redux";
import * as Sentry from "@sentry/react";

/**
 * @components
 */
import CampaignCard from "components/CampaignCard";
import CurrencyModal from "components/CurrencyModal";
import { renderFeeConfirmation } from "widgets/Donation/components/FeeConfirmation/renderFeeConfirmation";

/**
 * @helpers
 * */
import { usePrevious } from "helpers/previousHook";
import ScrollToError from "helpers/ScrollToError";

/**
 * @hooks
 * */
import useGetCountryIsoCode from "hooks/useGetCountryIsoCode";

/**
 * @redux
 * */
import {
  createPaymentIntent as createPaymentIntentAction,
  refreshPaymentIntent as refreshPaymentIntentAction,
  initializeDonationForm as initializeDonationFormAction,
  setCardField as setCardFieldAction,
  setOptionalTier as setOptionalTierAction,
  submitDirectDebit as submitDirectDebitAction,
  submitDonation as submitDonationAction,
  submitPaymentElement as submitPaymentElementAction,
  updatePaymentIntent as updatePaymentIntentAction,
  resetRefreshStripeCustomer as resetRefreshStripeCustomerAction,
  submitFailure as submitFailureAction,
} from "widgets/Donation/store/state";

/**
 * @forms
 * */
import { getInitialValues } from "widgets/Donation/utils/getInitialValues";
import updatePrefillFields from "widgets/Donation/utils/updatePrefillFields";
import { handleValidation, validate } from "widgets/Donation/utils/validation";

/**
 * @utility
 * */
import { buildArrayOfDonationComponents } from "widgets/Donation/utils/buildArrayOfDonationComponents";
import { closeFeeConfirmation, updateFeeAmounts } from "widgets/Donation/utils/feeHelpers";
import { fundraiserChange } from "widgets/Donation/utils/fundraiserHelpers";
import { generateCampaignCard } from "widgets/Donation/utils/generateCampaignCard";
import { donationFormHasAddress } from "widgets/Donation/utils/fieldHelpers";

import {
  decodeRecipientOwner,
  donationFormEnabled,
  handleDonationAmountChanges,
  handlePaymentMethodChanges,
  hasStripe,
  isCustomTemplate,
  renderDonationFormError,
  shouldChangeCountryCode,
} from "widgets/Donation/utils/helpers";
// !change this
import { handlePaymentRequestSubmit } from "widgets/Donation/components/PaymentVendorInputs/createAutoPaymentRequest";

/**
 * @Stripe
 */
import {
  confirmPaymentIntent,
  handleDirectDebitPayment,
  handlePayPalSubmission,
  stripeRedirectToCheckout,
  submitPaymentElementPaymentIntent,
  submitPaymentIntent,
  validateToken,
} from "widgets/Donation/utils/paymentSubmissionHelpers";

import { hasPerk, isNotPerkOrImpactLevel } from "widgets/Donation/utils/perkHelpers";

/**
 * @language
 */
import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";
import { Interweave } from "interweave";
import useUpdateBlockPayments from "./hooks/useUpdateBlockPayments";
import "./_style.scss";
import useGetPaymentMethodTypes from "./hooks/useGetPaymentMethodTypes";
import { getPaymentMethodTypes } from "./utils/getPaymentMethodOptions";
import TurnstileWrapper from "../../components/TurnstileWrapper";
import { createConfirmationToken } from "./utils/paymentSubmissionHelpers";

let updatedFees;

const setUpdatedFees = (feeInfo) => {
  updatedFees = feeInfo;
};

export const Donation = ({
  optionalTier,
  setOptionalTier,
  setCardField,
  donation,
  createPaymentIntent,
  isSubmitting,
  updatePaymentIntent,
  submitDonation,
  donationFormData,
  submitDirectDebit,
  directDebitSession,
  customContent,
  initializeDonationForm,
  turnstileKey,
  submitChangeCurrency,
  gqlLoadingErrors,
  loginUser,
  logoutUser,
  submitPaymentElement,
  isBlocked,
  shouldRefreshCustomer,
  refreshPaymentIntent,
  isIntentRefreshing,
  resetRefreshStripeCustomer,
  submitFailure,
}) => {
  const stripe = useStripe();
  const [paymentElements, setPaymentElements] = useState();
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const [currencyModalStatus, setCurrencyModalStatus] = useState(false);
  const [attemptedPaymentIntentCreation, setAttemptedPaymentIntentCreation] = useState();
  const [initializedForm, setInitializedForm] = useState();
  const [showDonation, setShowDonation] = useState();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [feeConfirmation, setFeeConfirmation] = useState();
  const [processingSubmit, setProcessingSubmit] = useState();
  const [donationValues, setDonationValues] = useState();
  const [isPaymentRequestMethod, setIsPaymentRequestMethod] = useState();
  const [foreignCurrencyApplePay, setIsForeignCurrencyApplePay] = useState(); // eslint-disable-line no-unused-vars
  const [canMakePaymentType, setCanMakePaymentType] = useState();
  const [target, setTarget] = useState();
  const [hasBouncedToFirst, setHasBouncedToFirst] = useState();
  const [captchaToken, setCaptchaToken] = useState();
  const [cardError, setCardError] = useState();
  const [isTurnstileInteractive, setIsTurnstileInteractive] = useState();
  const previousDonationFormData = usePrevious(donationFormData);
  const { translate: t } = useLanguageProvider();
  const { getCountryIsoCode } = useGetCountryIsoCode();
  const { updateBlockPayments } = useUpdateBlockPayments();

  const paymentMethodTypes = useGetPaymentMethodTypes(donationFormData);

  const formRef = useRef();

  const handlePaymentRequestPaymentMethodRef = useRef();
  handlePaymentRequestPaymentMethodRef.current = async (ev) => {
    // if payments are blocked, do nothing
    if (isBlocked) {
      return;
    }

    const cardBrand = ev.paymentMethod?.card?.brand?.toLowerCase();
    if (cardBrand === "discover" || cardBrand.includes("discover")) {
      ev.complete("fail");
      const isApplePayText = canMakePaymentType && canMakePaymentType.applePay ? " in Apple Pay" : "";
      setCardError(`Discover cards are not supported${isApplePayText}. Please use a different card or payment method.`);
      return;
    }

    const isWalletPayment = ["google_pay", "apple_pay"].includes(
      ev.paymentMethod?.card?.wallet?.type,
    );

    const isValidToken = await validateToken(
      ev.paymentMethod,
      donationFormData,
      donationValues,
      donationFormContext,
      handlers,
      updatedFees,
    );

    let walletFeeCalculations = {};

    try {
      if (isWalletPayment && isValidToken?.updatedFeeConfirmation) {
        walletFeeCalculations = updateFeeAmounts(
          true,
          isValidToken.updatedFeeConfirmation,
          null, // setFieldValue is null to avoid state update
        );
      }

      if (isValidToken.isValid || updatedFees?.isSubmittingFromConfirmation) {
        handlers.setIsPaymentRequestMethod(true);
        handlers.updatePaymentIntent({
          ...donationValues,
          ...updatedFees,
          ...donationFormData,
          ...donation,
          ...walletFeeCalculations,
          closeFeeConfirmation: closeFeeConfirmation(handlers),
          doSubmit: true,
          fromPaymentRequestButton: true,
          paymentIntentSecret: donation.paymentIntentsId,
          paymentIntentComplete: ev.complete,
          paymentMethod: ev.paymentMethod,
          stripeCardBrand: ev.paymentMethod.card.brand,
          stripeCardCountry: ev.paymentMethod.card.country,
          stripe,
          submitPaymentIntent: handlers.confirmPaymentIntent,
          cardErrorHandler,
        });
      } else {
        if (!isWalletPayment) {
          ev.complete("fail");
        } else {
          // This force closes the wallet pay window without showing the error.
          // Need to test with Apple pay
          ev.complete("success");
        }
        updateFeeAmounts(
          true,
          isValidToken.updatedFeeConfirmation,
          formRef.current.setFieldValue,
        );
      }
    } catch (error) {
      // Catch a general payment error in case anything unhandled comes up, and log it with Sentry so we can investigate
      ev.complete("fail");
      setCardError("There was an error processing your payment. Please try again.");
      Sentry.captureException(new Error(error), {
        contexts: {
          data: ev,
        },
      });
    }
  };

  useEffect(() => {
    if (hasPerk) {
      setShowDonation(false);
    }

    if (isNotPerkOrImpactLevel(donationFormData) || !hasPerk) {
      setShowDonation(true);
    }

    setTarget(donationFormData?.target || donationFormData?.recipient);
  }, [donationFormData]);

  /* eslint react-hooks/exhaustive-deps: "off" */
  useEffect(() => {
    if (directDebitSession === "completed") {
      const directDebitCheckoutSession = donationFormData.directDebitCheckoutSession;
      stripeRedirectToCheckout({ stripe, directDebitCheckoutSession });
    }
  }, [directDebitSession]);

  const createIntentCall = useCallback(
    (captchaToken, paymentMethodTypes) => {
      createPaymentIntent(
        donationFormData.paymentVendors.filter((vendor) =>
          vendor.includes("stripe"))[0],
        donationFormData.recipientId,
        donationFormData.feeStructure.currency,
        captchaToken,
        paymentMethodTypes,
      );
    },
    [
      donationFormData.paymentVendors,
      donationFormData.recipientId,
      donationFormData.feeStructure,
      createPaymentIntent,
    ],
  );

  useEffect(() => {
    if (
      donation?.paymentIntentsId === null
      && hasStripe(donationFormData)
      && !attemptedPaymentIntentCreation
      && captchaToken
      && paymentMethodTypes
    ) {
      setAttemptedPaymentIntentCreation(true);
      createIntentCall(captchaToken, paymentMethodTypes);
    }

    if (
      donationFormData
      && !initializedForm
      && !hasStripe(donationFormData)
      && captchaToken
    ) {
      initializeDonationForm();
      setInitializedForm(true);
    }
  }, [
    donation,
    createPaymentIntent,
    setInitializedForm,
    captchaToken,
  ]);

  useEffect(() => {
    if (stripe && hasStripe(donationFormData)) {
      /**
       * checks if apple pay and google pay are available
       */
      const paymentRequest = stripe.paymentRequest({
        country: shouldChangeCountryCode(
          donationFormData.feeStructure.countryCode,
        ),
        currency: donationFormData.feeStructure.currency,
        total: {
          label: `${decodeRecipientOwner(donationFormData.recipientOwner)}`,
          amount: 0,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      paymentRequest.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(paymentRequest);
          setCanMakePaymentType(result);
        }
      });
    }
  }, [stripe]);

  useEffect(() => {
    if (!paymentRequest || !donationValues) {
      return;
    }

    const listener = (ev) => handlePaymentRequestPaymentMethodRef.current(ev);

    paymentRequest.on("paymentmethod", listener);

    return () => {
      paymentRequest.off("paymentmethod", listener);
    };
  }, [
    paymentRequest,
    donationValues,
  ]);

  useEffect(() => {
    if (!previousDonationFormData) {
      return;
    }

    if (
      previousDonationFormData.feeStructure !== donationFormData.feeStructure
      && donationFormData.exchangeRate
    ) {
      const updatedDonationAmount = calculateUpdatedDonationAmount();
      handleDonationAmountChanges(
        updatedDonationAmount,
        donationFormData,
        formRef.current,
        optionalTier,
      );
    }

    if (previousDonationFormData.perks !== donationFormData.perks) {
      const updatedPerksCart = {
        ...formRef.current.values.perksCart,
        ...donationFormData.perks,
      };
      formRef.current.setFieldValue("perksCart", updatedPerksCart);
    }

    if (previousDonationFormData.prefill !== donationFormData.prefill) {
      updatePrefillFields(donationFormData.prefill, formRef.current);
    }
  }, [previousDonationFormData, donationFormData]);

  const calculateUpdatedDonationAmount = () => {
    const hasPerks = !!donationFormData?.perks;
    const hasImpactLevels = donationFormData.levels && donationFormData.levels.length > 0;

    if (!hasPerks && !hasImpactLevels) {
      return formRef.current.values.donationAmount;
    }

    return Number(
      formRef.current.values.donationAmount * donationFormData.exchangeRate,
    ).toFixed(0);
  };

  const handlePaymentElementSubmission = (
    formValues,
    stripe,
    elements,
    donationFormData,
    donationFormContext,
    handlers,
  ) => {
    const {
      closeFeeConfirmation,
      updateBlockPayments,
    } = handlers;

    setIsPaymentRequestMethod(false);
    submitPaymentElement({
      ...formValues,
      ...donationFormData,
      ...donation,
      elements: paymentElements,
      closeFeeConfirmation: closeFeeConfirmation(handlers),
      updateBlockPayments,
      doSubmit: true,
      fromPaymentRequestButton: false,
      paymentIntentSecret: donation.paymentIntentsId,
      stripe,
      submitPaymentElementPaymentIntent,
      cardErrorHandler,
      paymentMethodTypes: getPaymentMethodTypes(donationFormData),
    })
      .catch((error) => {
        setCardError(error ?? "There was an error submitting your payment.");
      });
  };

  const createSubmitConfirmationDetails = (values, formData) => {
    const billingDetails = {
      name: `${values.firstName} ${values.lastName}`,
      email: values.email,
    };

    if (formData?.donationFormFields && donationFormHasAddress(formData.donationFormFields)) {
      const addressCountry = values.addressCountry ? getCountryIsoCode(values.addressCountry) : "";

      billingDetails.address = {
        line1: values.addressLine1 ?? "",
        // line2 must be provided in the data, but we never collect it via Chuffed, so this is always blank
        line2: "",
        city: values.addressLocality ?? "",
        state: values.addressRegion ?? "",
        postal_code: values.addressPostcode ?? "",
        country: addressCountry,
      };
    }

    return billingDetails;
  };

  const handleSubmit = async (
    values,
    stripe,
    elements,
    donationFormData,
    donationFormContext,
    handlers,
    setFieldValue,
    setFieldTouched,
  ) => {
    // If the user is blocked, then don't submit anything
    if (isBlocked) {
      return;
    }

    setCardError(null);

    if (values.submitButton === "paymentElement") {
      if (!paymentElements || !isPaymentElementReady) {
        cardErrorHandler("Payment form is not ready yet. Please try again in a moment.");
        return;
      }

      setDonationValues(values);

      try {
        const token = await createConfirmationToken(
          paymentElements,
          stripe,
          createSubmitConfirmationDetails(values, donationFormData),
        );

        const isValidToken = await validateToken(
          token,
          donationFormData,
          values,
          donationFormContext,
          handlers,
          updatedFees,
        );

        if (isValidToken.isValid) {
          handlePaymentElementSubmission(
            values,
            stripe,
            paymentElements,
            donationFormData,
            donationFormContext,
            handlers,
            setFieldValue,
          );
        } else {
          updateFeeAmounts(
            true,
            isValidToken.updatedFeeConfirmation,
            setFieldValue,
          );
        }
      } catch (error) {
        cardErrorHandler(error.message);
      }
    }

    if (values.submitButton === "bacsDebit") {
      if (!paymentElements || !isPaymentElementReady) {
        cardErrorHandler("Payment form is not ready yet. Please try again in a moment.");
        return;
      }

      setDonationValues(values);

      handlePaymentElementSubmission(
        values,
        stripe,
        paymentElements,
        donationFormData,
        donationFormContext,
        handlers,
        setFieldValue,
      );
    }

    if (values.submitButton === "paymentRequest") {
      handlePaymentRequestSubmit(
        values,
        donationFormData,
        donationFormContext,
        handlers,
        setFieldTouched,
      );
    }

    if (values.submitButton === "paypal") {
      handlePayPalSubmission(
        values,
        donationFormData,
        donationFormContext,
        handlers,
        setFieldValue,
      );
    }
  };

  const handleCurrencyModalStatus = () => setCurrencyModalStatus(!currencyModalStatus);

  const resetFeesOnError = () => {
    if (feeConfirmation) {
      updateFeeAmounts(false, feeConfirmation, null);
    }
    setUpdatedFees(null);
  };

  const cardErrorHandler = (error) => {
    setCardError(error);
    resetFeesOnError();
    submitFailure(error);
  };

  const handlers = {
    handlePaymentMethodChanges: (
      paymentMethod,
      campaignData,
      formProps,
      optionalTier,
    ) => handlePaymentMethodChanges(paymentMethod, campaignData, formProps, optionalTier),
    handleDonationAmountChanges: (
      donationAmount,
      campaignData,
      formProps,
      optionalTier,
    ) => handleDonationAmountChanges(donationAmount, campaignData, formProps, optionalTier),
    handleStripeSubmit: () => { },
    openCurrencyModal: () => handleCurrencyModalStatus,
    saveUpdatedCurrency: () => { },
    setOptionalTier: (tier) => setOptionalTier(tier),
    setCardField: (field) => setCardField(field),
    submitDonation: (formData) => submitDonation(formData),
    updatePaymentIntent: (formData) => updatePaymentIntent(formData),
    setProcessingSubmit: () => setProcessingSubmit,
    setFeeConfirmation: (data) => setFeeConfirmation(data),
    setDonationValues: (values) => setDonationValues(values),
    submitPaymentIntent: (
      data,
      redirectUrl,
      closeFeeConfirmation,
      paymentMethod,
      complete,
      stripe,
      cardElem,
      cardErrorHandler,
    ) => submitPaymentIntent(
      data,
      redirectUrl,
      closeFeeConfirmation,
      paymentMethod,
      complete,
      stripe,
      cardElem,
      cardErrorHandler,
    ),
    closeFeeConfirmation: (handlers) => closeFeeConfirmation(handlers),
    updateFeeAmounts: (
      isNew,
      feeConfirmation,
      setFieldValue,
    ) => updateFeeAmounts(isNew, feeConfirmation, setFieldValue),
    setIsPaymentRequestMethod: (paymentRequestMethod) => setIsPaymentRequestMethod(paymentRequestMethod),
    setIsForeignCurrencyApplePay: () => setIsForeignCurrencyApplePay,
    confirmPaymentIntent: (
      data,
      redirectUrl,
      closeFeeConfirmation,
      paymentMethod,
      complete,
      stripe,
    ) => confirmPaymentIntent(data, redirectUrl, closeFeeConfirmation, paymentMethod, complete, stripe),
    fundraiserChange: (
      e,
      donationFormData,
      handlers,
      formProps,
    ) => fundraiserChange(e, donationFormData, handlers, formProps),
    setTarget: (target) => setTarget(target),
    handleDirectDebitPayment: (
      event,
      formValues,
      handlers,
      donationFormContext,
    ) => handleDirectDebitPayment(event, formValues, handlers, donationFormContext),
    submitDirectDebitAction: (formData) => submitDirectDebit({ ...formData, ...donationFormData }),
    setShowDonation: () => setShowDonation,
    setUpdatedFees: (feeInfo) => setUpdatedFees(feeInfo),
    setPaymentElements: (elements) => setPaymentElements(elements),
    setPaymentElementReady: (isReady) => setIsPaymentElementReady(isReady),
    logoutUser: () => logoutUser(),
    loginUser: (user) => loginUser(user),
    setCardError,
    updateBlockPayments: (email) => updateBlockPayments(email),
    refreshPaymentIntent: () => refreshPaymentIntent(donation?.paymentIntentsId, donationFormData.recipientId),
    resetRefreshStripeCustomer: () => resetRefreshStripeCustomer(),
  };

  const handleCurrencyChange = (countryIsoCode) => {
    submitChangeCurrency({
      variables: {
        feeStructureCountry: countryIsoCode,
        campaignCurrency:
          donationFormData.feeStructure.currency
          || donationFormData.campaignCurrency,
      },
    });
  };

  const donationFormContext = useMemo(
    () => ({
      hasPerk,
      optionalTier,
      paymentRequest,
      canMakePaymentType,
      donation,
      processingSubmit,
      feeConfirmation,
      isSubmitting,
      isPaymentRequestMethod,
      showDonation,
      target,
      gqlLoadingErrors,
      isBlocked,
      shouldRefreshCustomer,
      isIntentRefreshing,
      paymentElements,
      isPaymentElementReady,
    }),
    [
      optionalTier,
      paymentRequest,
      canMakePaymentType,
      donation,
      processingSubmit,
      feeConfirmation,
      isSubmitting,
      isPaymentRequestMethod,
      showDonation,
      target,
      gqlLoadingErrors,
      isBlocked,
      shouldRefreshCustomer,
      isIntentRefreshing,
      paymentElements,
      isPaymentElementReady,
    ],
  );

  const validateFormik = (values) =>
    handleValidation({
      values,
      donationFormData,
      validate,
      handleSubmitFailed: ScrollToError,
      hasBouncedToFirst,
      setHasBouncedToFirst,
    });

  const getCampaignCurrencyDetails = () => {
    const {
      financialInformation: {
        feeStructure: {
          state: {
            country_code: countryCode,
            currency,
          },
        },
      },
    } = donationFormData;

    return {
      currencyName: currency?.toUpperCase(),
      countryCode,
    };
  };

  return !donation.isLoading ? (
    <ElementsConsumer>
      {({ stripe, elements }) => (
        <div className="payment-wrapper">
          {donationFormEnabled(
            donationFormData,
            donation.paymentIntentsId,
          ) ? (
            <div className="payment donation-form">
              <Interweave content={customContent} className="payment__header" />
              <div className="payment__body">
                {!isCustomTemplate(donationFormData) && (
                <div className="payment__campaign">
                  <div
                    className="payment__campaign-card --v2 --desktop"
                    data-test="donation-form-campaign-card"
                  >
                    <CampaignCard
                      campaign={generateCampaignCard(
                        target,
                        donationFormData,
                      )}
                    />
                  </div>
                </div>
                )}

                <div className="payment__donate --v2">
                  <Formik
                    innerRef={formRef}
                    initialValues={getInitialValues(donationFormData)}
                    validate={validateFormik}
                    onSubmit={(
                      values,
                      { setFieldValue, setFieldTouched },
                    ) =>
                      handleSubmit(
                        values,
                        stripe,
                        elements,
                        donationFormData,
                        donationFormContext,
                        handlers,
                        setFieldValue,
                        setFieldTouched,
                      )}
                  >
                    {({ ...props }) => (
                      <Form data-test="donation-form">
                        {buildArrayOfDonationComponents(
                          donationFormData,
                          handlers,
                          props,
                          donationFormContext,
                        )}

                        {Object.keys(props.errors).length > 0 && props.submitCount > 0 && (
                          <p
                            className="donation-form__submission-error well"
                            data-test="donation-form-submission-error"
                          >
                            Please check your details.
                          </p>
                        )}
                      </Form>
                    )}
                  </Formik>
                  {(!isBlocked && cardError) && (
                  <p
                    className="donation-form__card-error well"
                    data-test="donation-form-card-error"
                  >
                    {cardError}
                    {" "}
                    Please try an alternative payment method.
                  </p>
                  )}
                  {isBlocked && (
                  <p
                    className="donation-form__card-error well"
                    data-test="donation-form-card-error"
                  >
                    You are unable to make a donation due to multiple blocked attempts
                  </p>
                  )}
                </div>
              </div>
            </div>
            ) : (
              renderDonationFormError(
                donationFormData.paymentVendors,
                donation.paymentIntentsId,
              )
            )}

          {feeConfirmation?.isVisible
                  && !feeConfirmation?.isSubmittingFromConfirmation
                  && renderFeeConfirmation(
                    donationFormData,
                    donationValues,
                    feeConfirmation,
                    donationFormContext,
                    handlers,
                    formRef.current.setFieldValue,
                  )}

          {currencyModalStatus && (
          <CurrencyModal
            currentCurrency={donationFormData.feeStructure.currency}
            currencyModaOpen={currencyModalStatus}
            exitCurrencyConversion={handleCurrencyModalStatus}
            submitChangeCurrency={handleCurrencyChange}
            campaignCurrency={getCampaignCurrencyDetails()}
          />
          )}

        </div>
      )}
    </ElementsConsumer>
  ) : (
    <div>
      {!isTurnstileInteractive && (
        <div className="payment__loader">
          <div className="payment__loader-content">
            <span className="payment__loader-text">
              {t("Loading payment form")}
              ...
            </span>
            <div className="payment__loader-spinner">
              <div />
              <div />
              <div />
              <div />
            </div>
          </div>
        </div>
      )}

      <div className={`turnstile__wrapper ${captchaToken ? "hidden" : ""}`}>
        <TurnstileWrapper
          onChange={(token) => setCaptchaToken(token)}
          captchaKey={turnstileKey}
          setTurnstileInteractive={setIsTurnstileInteractive}
          action="donationPage"
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  optionalTier: state.donation.optionalTier,
  donation: state.donation,
  directDebitSession: state.donation.directDebitSession,
  recaptchaErrorType: state.donation.recaptchaErrorType,
  isBlocked: state.donation.isBlocked ?? false,
  shouldRefreshCustomer: state.donation.shouldRefreshCustomer ?? false,
  isIntentRefreshing: state.donation.isIntentRefreshing ?? false,
});

const mapDispatchToProps = (dispatch) => ({
  updatePaymentIntent: (campaignId) =>
    dispatch(updatePaymentIntentAction(campaignId)),
  refreshPaymentIntent: (paymentIntentId, campaignId) =>
    dispatch(refreshPaymentIntentAction(paymentIntentId, campaignId)),
  createPaymentIntent: (
    vendor,
    campaignId,
    currency,
    captchaToken,
    captchaVersion,
    paymentMethodTypes,
  ) => dispatch(
    createPaymentIntentAction(vendor, campaignId, currency, captchaToken, captchaVersion, paymentMethodTypes),
  ),
  setOptionalTier: (tier) => dispatch(setOptionalTierAction(tier)),
  setCardField: (field) => dispatch(setCardFieldAction(field)),
  submitDonation: (formData) => dispatch(submitDonationAction(formData)),
  submitDirectDebit: (formData) => dispatch(submitDirectDebitAction(formData)),
  submitPaymentElement: (formData) => dispatch(submitPaymentElementAction(formData)),
  initializeDonationForm: () => dispatch(initializeDonationFormAction()),
  resetRefreshStripeCustomer: () => dispatch(resetRefreshStripeCustomerAction()),
  submitFailure: (error) => dispatch(submitFailureAction(error)),
});

const DonationConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(Donation);

export default DonationConnected;
